<template>
    <Dialog
        v-model:visible="visible"
        modal
        dismissable-mask
        :draggable="false"
        :show-header="false"
        position="top"
        class="command-palette"
        :style="{ width: '42rem', maxWidth: '95vw' }"
        @show="onShow"
        @hide="onHide"
    >
        <div class="command-palette__input">
            <i class="pi pi-search" />
            <input
                ref="inputEl"
                v-model="query"
                type="text"
                autocomplete="off"
                spellcheck="false"
                role="combobox"
                aria-expanded="true"
                aria-controls="command-palette-list"
                :placeholder="$t('core.command_palette_placeholder')"
                @keydown.down.prevent="move(1)"
                @keydown.up.prevent="move(-1)"
                @keydown.enter.prevent="runActive"
                @keydown.tab.prevent="cycleTab(1)"
                @keydown.left.prevent="cycleTab(-1)"
                @keydown.right.prevent="cycleTab(1)"
            />
            <span class="command-palette__kbd">esc</span>
            <button
                type="button"
                class="command-palette__close"
                :aria-label="$t('core.close')"
                :title="$t('core.close')"
                @click="visible = false"
            >
                <i class="pi pi-times" />
            </button>
        </div>

        <div class="command-palette__tabs">
            <button
                v-for="tab in tabs"
                :key="tab.key"
                type="button"
                class="command-palette__tab"
                :class="{ 'is-active': activeTab === tab.key }"
                @click="selectTab(tab.key)"
            >
                {{ $t(tab.label) }}
                <span v-if="tab.count > 0" class="command-palette__count">{{ tab.count }}</span>
            </button>
            <i v-if="loading" class="pi pi-spin pi-spinner command-palette__loading" />
        </div>

        <div id="command-palette-list" ref="listEl" class="command-palette__list" role="listbox">
            <template v-if="entries.length">
                <button
                    v-for="(entry, i) in entries"
                    :id="'command-palette-option-' + i"
                    :key="entry.key"
                    type="button"
                    role="option"
                    :aria-selected="i === activeIndex"
                    class="command-palette__item"
                    :class="{ 'is-active': i === activeIndex }"
                    @click="run(entry)"
                    @mousemove="activeIndex = i"
                >
                    <i :class="entry.icon" class="command-palette__item-icon" />
                    <span class="command-palette__item-body">
                        <span class="command-palette__item-title">{{ entry.title }}</span>
                        <span v-if="entry.subtitle" class="command-palette__item-subtitle">{{ entry.subtitle }}</span>
                    </span>
                    <span v-if="entry.badge" class="command-palette__item-badge">{{ entry.badge }}</span>
                </button>
            </template>
            <div v-else class="command-palette__empty">
                {{ activeTab === 'search' && query.length < MIN_SEARCH_LENGTH
                    ? $t('core.command_palette_keep_typing')
                    : $t('core.command_palette_no_results') }}
            </div>
        </div>

        <div class="command-palette__footer">
            <span><span class="command-palette__kbd">↑</span><span class="command-palette__kbd">↓</span> {{ $t('core.command_palette_navigate') }}</span>
            <span><span class="command-palette__kbd">↵</span> {{ $t('core.command_palette_open') }}</span>
            <span><span class="command-palette__kbd">tab</span><span class="command-palette__kbd">←</span><span class="command-palette__kbd">→</span> {{ $t('core.command_palette_switch') }}</span>
        </div>
    </Dialog>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import axios from 'axios'
import Dialog from 'primevue/dialog'
import { navPages } from '@/navigation/access'

const MIN_SEARCH_LENGTH = 2
const MAX_PER_TYPE = 5

const store = useStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()

const visible = ref(false)
const query = ref('')
const activeTab = ref('pages')
const activeIndex = ref(0)
const loading = ref(false)
const searchResult = ref({})
const inputEl = ref(null)
const listEl = ref(null)

const user = computed(() => store.getters['auth/user'])
const navItems = computed(() => store.getters['navigation/navItems'])

/* ------------------------------------------------------------------ pages */

// Extra destinations that live outside the nav tree (sidebar footer / logo).
const extraPages = [
    { label: 'core.home', icon: 'pi pi-fw pi-home', target: 'home', trail: [] },
    { label: 'core.settings', icon: 'pi pi-fw pi-cog', target: 'settings.profile', trail: [] }
]

const pages = computed(() => {
    const byTarget = new Map()
    for (const page of [...extraPages, ...navPages(navItems.value, user.value)]) {
        if (byTarget.has(page.target)) continue
        // The nav tree repeats the group label on its single child, so drop
        // trail segments that would only echo the page title.
        const trail = page.trail.filter((segment, i) => segment !== page.label && segment !== page.trail[i - 1])
        byTarget.set(page.target, {
            key: 'page:' + page.target,
            icon: page.icon || 'pi pi-fw pi-file',
            title: t(page.label),
            subtitle: trail.map((segment) => t(segment)).join(' › '),
            to: { name: page.target }
        })
    }
    return [...byTarget.values()]
})

/* --------------------------------------------------------------- matching */

function score(text, needle) {
    const haystack = (text || '').toLowerCase()
    if (!haystack) return -1

    const index = haystack.indexOf(needle)
    if (index === 0) return 1000
    if (index > 0) {
        // A match at a word boundary beats one buried inside a word.
        const boundary = /[\s›/_-]/.test(haystack[index - 1])
        return (boundary ? 900 : 700) - index
    }

    // Fall back to a subsequence match ("cusinv" → "customer invoices").
    let cursor = 0
    let gaps = 0
    for (const char of needle) {
        const found = haystack.indexOf(char, cursor)
        if (found === -1) return -1
        gaps += found - cursor
        cursor = found + 1
    }
    return 400 - gaps
}

const pageMatches = computed(() => {
    const needle = query.value.trim().toLowerCase()
    if (!needle) return pages.value

    return pages.value
        .map((page) => ({ page, score: Math.max(score(page.title, needle), score(page.subtitle, needle) - 200) }))
        .filter((match) => match.score > -1)
        .sort((a, b) => b.score - a.score || a.page.title.localeCompare(b.page.title))
        .map((match) => match.page)
})

/* --------------------------------------------------------------- search */

const canSeeCompanyData = computed(() => {
    const u = user.value
    return !!u && (u.is_super_user || u.roles?.includes('CompanyAdmin'))
})

function plain(html, limit = 90) {
    const text = (html || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
    return text.length > limit ? text.substring(0, limit) + '…' : text
}

// Maps every bucket returned by SearchController::fullTextSearch to a palette row.
const searchTypes = [
    {
        key: 'projects', label: 'project.projects', icon: 'pi pi-fw pi-briefcase',
        title: (p) => p.name,
        to: (p) => ({ name: 'project_management.project_detail', params: { id: p.id } })
    },
    {
        key: 'issues', label: 'project.issue', icon: 'pi pi-fw pi-check-square',
        title: (i) => '#' + i.public_id + ': ' + i.title,
        subtitle: (i) => i.project?.name,
        to: (i) => ({ name: 'project_management.project_issue_detail', params: { issue_id: i.public_id, id: i.project_id } })
    },
    {
        key: 'messages', label: 'project.messages', icon: 'pi pi-fw pi-comment',
        title: (m) => plain(m.message),
        subtitle: (m) => (m.issue ? '#' + m.issue.public_id + ': ' + m.issue.title : ''),
        enabled: (m) => !!m.issue,
        to: (m) => ({ name: 'project_management.project_issue_detail', params: { issue_id: m.issue.public_id, id: m.issue.project_id } })
    },
    {
        key: 'wiki', label: 'project.wiki', icon: 'pi pi-fw pi-book',
        title: (w) => plain(w.content),
        subtitle: (w) => w.project?.name,
        to: (w) => ({ name: 'project_management.project_wiki', params: { id: w.project_id, slug: w.slug } })
    },
    {
        key: 'documents', label: 'documents.module', icon: 'pi pi-fw pi-folder',
        title: (d) => d.name,
        subtitle: (d) => d.description,
        to: (d) => ({ name: 'documents.edit', params: { id: d.id } })
    },
    {
        key: 'mailMessages', label: 'mail.email_messages', icon: 'pi pi-fw pi-envelope',
        title: (m) => m.subject,
        subtitle: (m) => m.from_name || m.from_email,
        enabled: (m) => !!m.conversation_id,
        to: (m) => ({ name: 'mail.conversation', params: { id: m.conversation_id } })
    },
    {
        key: 'customers', label: 'sales.customers', icon: 'pi pi-fw pi-users',
        visible: () => canSeeCompanyData.value,
        title: (c) => c.name,
        to: (c) => ({ name: 'sales.customers.edit', params: { id: c.id } })
    },
    {
        key: 'customerOrders', label: 'sales.customer_orders', icon: 'pi pi-fw pi-shopping-cart',
        title: (o) => o.po_number || o.title,
        subtitle: (o) => [o.title, o.status].filter(Boolean).join(' · '),
        to: (o) => ({ name: 'sales.customer_orders.detail', params: { id: o.id } })
    },
    {
        key: 'quotations', label: 'sales.quotations', icon: 'pi pi-fw pi-file-edit',
        title: (q) => q.quotation_number || q.title,
        subtitle: (q) => [q.title, q.status].filter(Boolean).join(' · '),
        to: (q) => ({ name: 'sales.quotations.edit', params: { id: q.id } })
    },
    {
        key: 'customerInvoices', label: 'accounting.customer_invoices', icon: 'pi pi-fw pi-money-bill',
        title: (i) => i.invoice_number,
        subtitle: (i) => i.description,
        to: (i) => ({ name: 'accounting.customer-invoices.edit', params: { id: i.id } })
    },
    {
        key: 'companies', label: 'project.companies', icon: 'pi pi-fw pi-building',
        visible: () => canSeeCompanyData.value,
        title: (c) => c.name,
        to: (c) => ({ name: 'system.company_detail', params: { id: c.id } })
    }
]

const searchMatches = computed(() => {
    const entries = []
    for (const type of searchTypes) {
        if (type.visible && !type.visible()) continue
        const rows = (searchResult.value[type.key] || []).filter((row) => !type.enabled || type.enabled(row))
        for (const row of rows.slice(0, MAX_PER_TYPE)) {
            entries.push({
                key: type.key + ':' + row.id,
                icon: type.icon,
                title: type.title(row) || '—',
                subtitle: type.subtitle ? type.subtitle(row) : '',
                badge: t(type.label),
                to: type.to(row)
            })
        }
    }

    if (entries.length) {
        entries.push({
            key: 'search:all',
            icon: 'pi pi-fw pi-search',
            title: t('core.command_palette_all_results'),
            subtitle: '',
            to: { name: 'system.search', query: { q: query.value.trim() } }
        })
    }
    return entries
})

let searchTimer = null
let searchAbort = null

async function fetchSearch() {
    const q = query.value.trim()
    if (q.length < MIN_SEARCH_LENGTH) {
        searchResult.value = {}
        loading.value = false
        return
    }

    searchAbort?.abort()
    const controller = new AbortController()
    searchAbort = controller
    loading.value = true
    try {
        const { data } = await axios.post('/api/search', { q }, { signal: controller.signal })
        searchResult.value = data || {}
    } catch (error) {
        if (!axios.isCancel(error)) {
            console.error(error)
            searchResult.value = {}
        }
    } finally {
        // Only the newest request may clear the spinner.
        if (searchAbort === controller) loading.value = false
    }
}

watch(query, () => {
    activeIndex.value = 0
    clearTimeout(searchTimer)
    searchTimer = setTimeout(fetchSearch, 300)
})

/* ------------------------------------------------------------------ tabs */

const tabs = computed(() => [
    { key: 'pages', label: 'core.command_palette_pages', count: pageMatches.value.length },
    { key: 'search', label: 'system.search_results', count: searchMatches.value.length }
])

const entries = computed(() => (activeTab.value === 'pages' ? pageMatches.value : searchMatches.value))

// Results can shrink under the cursor while a search resolves.
watch(entries, (list) => {
    if (activeIndex.value >= list.length) activeIndex.value = Math.max(0, list.length - 1)
})

function selectTab(key) {
    activeTab.value = key
    activeIndex.value = 0
    inputEl.value?.focus()
}

function cycleTab(delta) {
    const keys = tabs.value.map((tab) => tab.key)
    const next = (keys.indexOf(activeTab.value) + delta + keys.length) % keys.length
    selectTab(keys[next])
}

/* -------------------------------------------------------------- keyboard */

function move(delta) {
    if (!entries.value.length) return
    const count = entries.value.length
    activeIndex.value = (activeIndex.value + delta + count) % count
    nextTick(scrollActiveIntoView)
}

function scrollActiveIntoView() {
    listEl.value?.querySelector('.command-palette__item.is-active')?.scrollIntoView({ block: 'nearest' })
}

function run(entry) {
    visible.value = false
    router.push(entry.to).catch(() => {})
}

function runActive() {
    const entry = entries.value[activeIndex.value]
    if (entry) run(entry)
}

/* ----------------------------------------------------------- open / close */

function isTypingTarget(target) {
    if (!target || !target.tagName) return false
    if (['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) return true
    if (target.isContentEditable) return true
    return !!target.closest?.('[contenteditable="true"], .ProseMirror')
}

function open() {
    query.value = ''
    searchResult.value = {}
    activeTab.value = 'pages'
    activeIndex.value = 0
    visible.value = true
}

function onShow() {
    nextTick(() => inputEl.value?.focus())
}

function onHide() {
    clearTimeout(searchTimer)
    searchAbort?.abort()
    loading.value = false
}

function onKeydown(event) {
    // A page that handles the key itself (the mail view's "/" and its composer's Ctrl+K) comes first
    if (event.defaultPrevented) return
    if ((event.metaKey || event.ctrlKey) && !event.altKey && event.key?.toLowerCase() === 'k') {
        event.preventDefault()
        visible.value ? (visible.value = false) : open()
        return
    }
    if (visible.value || event.metaKey || event.ctrlKey || event.altKey) return
    if (event.key === '/' && !isTypingTarget(event.target)) {
        event.preventDefault()
        open()
    }
}

watch(() => route.fullPath, () => { visible.value = false })

onMounted(() => {
    window.addEventListener('keydown', onKeydown)
    // Without a keyboard: the search on the Android app's "More" tab (AppMore)
    window.addEventListener('command-palette-open', open)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKeydown)
    window.removeEventListener('command-palette-open', open)
    onHide()
})

defineExpose({ open })
</script>

<style lang="scss">
.command-palette.p-dialog {
    margin-top: 8vh;
    border-radius: 12px;
    overflow: hidden;

    .p-dialog-content {
        padding: 0;
        display: flex;
        flex-direction: column;
        max-height: 70vh;
    }
}

.command-palette__input {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.9rem 1rem;
    border-bottom: 1px solid var(--surface-border);

    > .pi-search {
        color: var(--text-color-secondary);
    }

    input {
        flex: 1;
        border: 0;
        outline: 0;
        background: transparent;
        color: var(--text-color);
        font-size: 1.05rem;
        line-height: 1.4;

        &::placeholder {
            color: var(--text-color-secondary);
        }
    }
}

.command-palette__close {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    padding: 0;
    border: 0;
    border-radius: 6px;
    background: transparent;
    color: var(--text-color-secondary);
    cursor: pointer;

    &:hover {
        background: var(--surface-hover);
        color: var(--text-color);
    }

    .pi {
        font-size: 0.85rem;
    }
}

.command-palette__tabs {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.75rem;
    border-bottom: 1px solid var(--surface-border);
}

.command-palette__tab {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.3rem 0.7rem;
    border: 0;
    border-radius: 999px;
    background: transparent;
    color: var(--text-color-secondary);
    font-size: 0.85rem;
    cursor: pointer;

    &:hover {
        background: var(--surface-hover);
    }

    &.is-active {
        background: var(--primary-color);
        color: #fff;
    }
}

.command-palette__count {
    font-size: 0.72rem;
    opacity: 0.8;
}

.command-palette__loading {
    margin-left: auto;
    color: var(--text-color-secondary);
    font-size: 0.85rem;
}

.command-palette__list {
    flex: 1;
    overflow-y: auto;
    padding: 0.4rem;
}

.command-palette__item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    width: 100%;
    padding: 0.55rem 0.7rem;
    border: 0;
    border-radius: 8px;
    background: transparent;
    color: var(--text-color);
    text-align: left;
    cursor: pointer;

    &.is-active {
        background: var(--surface-hover);
    }
}

.command-palette__item-icon {
    color: var(--text-color-secondary);
    flex: 0 0 auto;
}

.command-palette__item-body {
    display: flex;
    flex-direction: column;
    min-width: 0;
    flex: 1;
}

.command-palette__item-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.command-palette__item-subtitle {
    font-size: 0.78rem;
    color: var(--text-color-secondary);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.command-palette__item-badge {
    flex: 0 0 auto;
    font-size: 0.7rem;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    border: 1px solid var(--surface-border);
    color: var(--text-color-secondary);
}

.command-palette__empty {
    padding: 1.5rem 1rem;
    text-align: center;
    color: var(--text-color-secondary);
    font-size: 0.9rem;
}

.command-palette__footer {
    display: flex;
    gap: 1rem;
    padding: 0.5rem 1rem;
    border-top: 1px solid var(--surface-border);
    font-size: 0.75rem;
    color: var(--text-color-secondary);
}

/* A touch screen has none of the keys the hints name */
@media (pointer: coarse) {
    .command-palette__footer,
    .command-palette__input > .command-palette__kbd {
        display: none;
    }
}

.command-palette__kbd {
    display: inline-block;
    padding: 0 0.3rem;
    margin-right: 0.2rem;
    border: 1px solid var(--surface-border);
    border-radius: 4px;
    font-size: 0.7rem;
    line-height: 1.35;
}
</style>
